import { Component, OnInit } from '@angular/core';

import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { HelperService, HTTPService, DataService } from 'app/services';

@Component({
  selector: 'app-location-add',
  templateUrl: './location-add.component.html',
  styleUrls: ['./location-add.component.scss'],
  providers: [DialogService],
})
export class LocationAddComponent implements OnInit {
  // current location id, only available in edit mode
  private locationId: string = '';

  // global data
  public warehouses: any[] = [];
  public location: any = {
    isActive: true,
  };

  // modal presentation
  public title: string = 'Location';
  public subTitle: string = 'Add new location';

  // operation indications/flags
  public isCreatingOrUpdatingLocation: boolean = false;
  public isWarehouseEditMode: boolean = false;
  public isAisleEditMode: boolean = false;
  public isSavingWarehouse: boolean = false;
  public isSavingAisle: boolean = false;
  isOpenFromPO: boolean = false;
  isMultiLocationEnabled: boolean = false;
  
  // temporary holders
  public updatedWarehouse: any;
  public updatedAisle: any;

  // field errors
  public fieldErrors = {
    warehouse: '',
    aisle: '',
    bin: '',
  };

  // fields for data service
  poItemIndex: number = -1;

  constructor(
    private httpService: HTTPService,
    public helperService: HelperService,
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dataService: DataService
  ) {}

  ngOnInit() {
    this.initModalData();
  }

  /**
   * Initate required modal data
   */
  private async initModalData(): Promise<void> {
    try {
      // get location id if provided
      const { locationId, data } = this.dynamicDialogConfig.data;
      const { isMultiLocationEnabled } = this.helperService.getCompany();

      this.isMultiLocationEnabled = isMultiLocationEnabled;
      this.locationId = locationId;

      if (data && data.index >= 0) {
        this.isOpenFromPO = true;
        this.poItemIndex = data.index;
      }

      if (isMultiLocationEnabled && !this.locationId) {
        const warehouse: any = await this.httpService.getWarehouse({ fetchShopWarehouse: true });

        if (warehouse) {
          this.handleChange('warehouse', this.formatWarehouse(warehouse));
        }
      } else {
        await this.getWarehouses();
      }

      if (this.locationId) {
        this.getLocationData();
        this.subTitle = 'Edit location';
      }
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Format a warehouse into desired format
   */
  private formatWarehouse(warehouse): any {
    warehouse.id = warehouse._id;
    warehouse.aisles = warehouse.aisles || [];
    warehouse.aisles = warehouse.aisles.map((a) => {
      a.id = a._id;
      return a;
    });
    return warehouse;
  }

  /**
   * Get warehouses list
   */
  private async getWarehouses(): Promise<void> {
    try {
      const warehouses: any = await this.httpService.getWarehouses();
      this.warehouses = (warehouses.records || []).map((wh) => {
        return this.formatWarehouse(wh);
      });
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Get location data
   */
  private async getLocationData(): Promise<void> {
    try {
      const data: any = await this.httpService.getLocationById(this.locationId);
      this.title = `${data.title}`;
      this.location = {
        ...data,
        warehouse: {
          name: data.warehouse,
          id: data.warehouseId,
        },
        aisle: {
          name: data.aisle,
          id: data.aisleId,
        },
      };

      // populate existing aisles for a selected warehouse
      this.warehouses.forEach((w) => {
        if (w._id === data.warehouseId) {
          this.location.warehouse.aisles = w.aisles || [];
        }
      });
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Create a location title based on fields selected
   */
  private createLocationTitle(): void {
    const warehouseTitle: string = (this.location.warehouse && this.location.warehouse.name) || '';

    const aisleTitle: string = (this.location.aisle && this.location.aisle.name) || '';

    const binTitle: string = this.location.bin || '';

    this.location.title = `${warehouseTitle}-${aisleTitle}-${binTitle}`;

    // also update modal title
    this.title = `${warehouseTitle}${aisleTitle ? '-' : ''}${aisleTitle}${binTitle ? '-' : ''}${binTitle}`;
  }

  /**
   * On field changes
   */
  public handleChange(fieldName, event: any): void {
    let finalData = event;

    // if select is from create warehouse/aisle dont use it
    if (event.isFromEditableField) return;

    // if warehouse, validate required data for warehouse
    if (fieldName === 'warehouse') {
      if (!event || !event.name || !event.id) {
        this.fieldErrors.warehouse = 'Warehouse is invalid';
      } else {
        this.fieldErrors.warehouse = '';
        // since this is a valid case, that means warehouse is changed
        // reset aisles array if already selected warehouse is not selected again
        if (this.location.aisle && this.location.warehouse && this.location.warehouse.id !== event.id) {
          this.location.aisle = '';
        }
      }
      this.location[fieldName] = null;
    } else if (fieldName === 'aisle') {
      if (!event || !event.name || !event.id) {
        this.fieldErrors.aisle = 'Aisle is invalid';
      } else {
        this.fieldErrors.aisle = '';
      }
    } else if (fieldName === 'bin') {
      if (!event) {
                this.fieldErrors.bin = 'Bin is required';
      } else {
        this.fieldErrors.bin = '';
      }
    }

    // if changed field has an error dont set its value
    if (this.fieldErrors[fieldName]) finalData = '';
    this.location[fieldName] = finalData;
    this.location = this.helperService.deepCopy(this.location);
    this.createLocationTitle();
  }

  /**
   * Close Modal
   * @param isSuccess
   */
  public handleClose(isSuccess: boolean = false, data: any = null): void {
    this.dynamicDialogRef.close({ isSuccess, data });
  }

  /**
   * Validate all fields
   */
  private validateFields(): boolean {
    try {
      let isValid: boolean = true;

      if (!this.location.warehouse || !this.location.warehouse.name || !this.location.warehouse.id) {
        this.fieldErrors.warehouse = 'Warehouse is required';
        isValid = false;
      }

      if (!this.location.aisle || !this.location.aisle.name || !this.location.aisle.id) {
        this.fieldErrors.aisle = 'Aisle is required';
        isValid = false;
      }

      if (!this.location.bin) {
        this.fieldErrors.bin = 'Bin is required';
        isValid = false;
      }

      return isValid;
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
      return false;
    }
  }

  /**
   * Handle submit button press
   */
  public async handleSubmit(): Promise<void> {
    try {
      if (this.isCreatingOrUpdatingLocation) return;

      // validate form fields
      if (!this.validateFields()) return;

      // set processing true
      this.isCreatingOrUpdatingLocation = true;

      this.createLocationTitle();

      const location: any = {
        title: this.location.title,
        isActive: this.location.isActive,
        warehouse: this.location.warehouse.name,
        warehouseId: this.location.warehouse.id,
        aisle: this.location.aisle.name,
        aisleId: this.location.aisle.id,
        bin: this.location.bin,
      };

      if (this.locationId) {
        await this.httpService.updateInvLocation(this.locationId, location);
      } else {
        const nLocation: any = await this.httpService.createInvLocation(location);

        if (nLocation) {
          location.locationId = nLocation._id;
          location.name = nLocation.title;
        }

        this.dataService.sendData(
          this.dataService.typePassData('po-service-item-location', {
            actionType: 'create',
            location: {
              locationId: nLocation._id,
              name: nLocation.title,
            },
            index: this.poItemIndex,
          })
        );
      }

      // close modal on succss
      this.handleClose(true, location);
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    } finally {
      this.isCreatingOrUpdatingLocation = false;
    }
  }

  /**
   * On Create Warehouse
   * @param param0
   */
  public async handleCreateWarehouse({ name }): Promise<void> {
    try {
      if (!name.trim()) {
        throw new Error('Enter valid warehouse name');
      }
      const warehouse: any = await this.httpService.createWarehouse({ name });

      // refresh warehouse list, and select newly created warehouse
      this.handleChange('warehouse', this.formatWarehouse(warehouse));
      this.warehouses = [...this.warehouses, this.formatWarehouse(warehouse)];
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * On Create Aisle
   * @param param0
   */
  public async onCreateAisle({ name }): Promise<void> {
    try {
      if (!name.trim()) {
        throw new Error('Enter valid aisle name');
      }

      // just an additional check to verify that warehouse id is present
      if (!this.location.warehouse.id) {
        throw new Error('Warehouse not selected or invalid warehouse');
      }

      const warehouse: any = await this.httpService.createAisle(this.location.warehouse.id, { name });

      // update current selected warehouse
      this.handleChange('warehouse', this.formatWarehouse(warehouse));

      // find current aisle created and select it automatically
      // look for aisle that was created and set is as default
      (this.location.warehouse.aisles || []).forEach((a) => {
        if (a.name === name) this.handleChange('aisle', a);
      });

      // update current warehouse in warehouse list
      this.warehouses = this.warehouses.map((w) => {
        if (w.id === warehouse._id) return this.location.warehouse;
        return w;
      });
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Switch warehouse/aisle edit mode on
   */
  public showEditOption(fieldName: string, entity: any): void {
    try {
      // check if required fields present
      if (!entity.id || !entity.name) {
        throw new Error('Can\t edit invalid item');
      }

      if (fieldName === 'warehouse') {
        this.isWarehouseEditMode = true;
        this.updatedWarehouse = this.helperService.deepCopy(entity);
      } else if (fieldName === 'aisle') {
        this.isAisleEditMode = true;
        this.updatedAisle = this.helperService.deepCopy(entity);
      }
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Turn of warehouse/aisle edit mode
   */
  public handleEditModeCancel(fieldName: string, event: Event): void {
    event.stopPropagation();
    if (fieldName === 'warehouse') {
      this.isWarehouseEditMode = false;
      this.updatedWarehouse = null;
    } else if (fieldName === 'aisle') {
      this.isAisleEditMode = false;
      this.updatedAisle = null;
    }
  }

  /**
   * Handle edit field value change
   */
  public handleEditFieldChange(fieldName: string, value: string) {
    try {
      if (fieldName === 'warehouse') {
        this.updatedWarehouse.name = value.trim();
      } else if (fieldName === 'aisle') {
        this.updatedAisle.name = value.trim();
      }
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Update warehouse
   */
  public async updateWarehouse(event: Event): Promise<void> {
    try {
      if (!this.updatedWarehouse.name.trim()) {
        throw new Error('Invalid warehouse name');
      }

      const warehouse: any = await this.httpService.updateWarehouse(this.updatedWarehouse.id, { name: this.updatedWarehouse.name.trim() });

      // if current selected warehouse is the one that was updated
      // update current warehouse
      if (this.location.warehouse && this.location.warehouse && this.location.warehouse.id === this.updatedWarehouse.id) {
        this.handleChange('warehouse', this.formatWarehouse(warehouse));
      }

      this.getWarehouses();

      // close the modal
      this.handleEditModeCancel('warehouse', event);
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /**
   * Update aisle
   */
  public async updateAisle(event: Event): Promise<void> {
    try {
      if (!this.updatedAisle.name.trim()) {
        throw new Error('Invalid aisle name');
      }

      // just an additional check to verify that warehouse id is present
      if (!this.location.warehouse.id) {
        throw new Error('Warehouse not selected or invalid warehouse');
      }
      if (!this.updatedAisle.id) {
        throw new Error('Invalid aisle to update');
      }

      const warehouse: any = await this.httpService.updateAisle(this.location.warehouse.id, this.updatedAisle.id, { name: this.updatedAisle.name.trim() });

      this.handleChange('warehouse', this.formatWarehouse(warehouse));

      // find current aisle created and update it
      (this.location.warehouse.aisles || []).forEach((a: any) => {
        if (a.id === this.updatedAisle.id) this.handleChange('aisle', a);
      });

      this.getWarehouses();

      // close the modal
      this.handleEditModeCancel('aisle', event);
    } catch (error) {
      console.error(error);
      this.helperService.showToaster('error', error);
    }
  }

  /** Setting active status of location */
  handleSwitchChange(isActive: boolean) {
    this.location.isActive = isActive;
  }
}
